import i18n from "../i18n";
import React, { useEffect, useState } from "react";
import { api, apiFileUrl, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableHead, TableHeader, TableRow, TableCell, TableBody } from "@/components/ui/table";
import { Plus, Trash2, Edit2, GripVertical, Settings2, Database, Cloud, Layers } from "lucide-react";
import CustomizationTab from "@/pages/CustomizationTab";
import { toast } from "sonner";
import DataExportTab from "@/pages/DataExportTab";
import GoogleDriveTab from "@/pages/GoogleDriveTab";
import { useAuth } from "@/context/AuthContext";
import SpeciesIconPicker from "@/components/SpeciesIconPicker";

// -------- Reusable list-CRUD builder for simple config lists --------
function ConfigTable({ title, testid, columns, items, onAdd, onEdit, onDelete, addLabel = "Add", deleteLabel, renderRow, renderActions }) {
    return (
        <Card className="p-6" data-testid={`config-${testid}`}>
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold">{title}</h3>
                <Button onClick={onAdd} className="h-11" data-testid={`add-${testid}-btn`}><Plus className="w-4 h-4 mr-2" />{addLabel}</Button>
            </div>
            <Table>
                <TableHeader>
                    <TableRow>{columns.map(c => <TableHead key={c}>{c}</TableHead>)}<TableHead className="text-right">{i18n.t("batch4.c3cd636a585b")}</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                    {items.map((it) => (
                        <TableRow key={it.id}>
                            {renderRow(it)}
                            <TableCell className="text-right space-x-2">
                                {renderActions?.(it)}
                                <Button size="sm" variant="outline" onClick={() => onEdit(it)} data-testid={`edit-${testid}-${it.id}`}><Edit2 className="w-4 h-4" /></Button>
                                <Button size="sm" variant="destructive" onClick={() => onDelete(it)} data-testid={`delete-${testid}-${it.id}`} aria-label={`${deleteLabel || "Delete"} ${it.name || "record"}`}>
                                    <Trash2 className={`w-4 h-4 ${deleteLabel ? "mr-1" : ""}`} />{deleteLabel || null}
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                    {items.length === 0 && <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-8 text-muted-foreground">{i18n.t("batch4.b2eefdbf3b29")}{addLabel}{i18n.t("batch4.28c7da5a0ed0")}</TableCell></TableRow>}
                </TableBody>
            </Table>
        </Card>
    );
}

const SPECIES_PRESETS = [
    { value: "pig", name: "Pig", icon: "🐖", days: 114 },
    { value: "chicken", name: "Chicken", icon: "🐔", days: 21 },
    { value: "duck", name: "Duck", icon: "🦆", days: 28 },
    { value: "goat", name: "Goat", icon: "🐐", days: 150 },
    { value: "cattle", name: "Cattle (Cow)", icon: "🐄", days: 283 },
    { value: "carabao", name: "Carabao (Water Buffalo)", icon: "🐃", days: 320 },
    { value: "sheep", name: "Sheep", icon: "🐑", days: 147 },
    { value: "rabbit", name: "Rabbit", icon: "🐇", days: 31 },
    { value: "quail", name: "Quail", icon: "🐦", days: 18 },
    { value: "turkey", name: "Turkey", icon: "🦃", days: 28 },
    { value: "tilapia", name: "Tilapia", icon: "🐟", days: 0 },
    { value: "catfish", name: "Catfish (Hito)", icon: "🐟", days: 0 },
    { value: "milkfish", name: "Milkfish (Bangus)", icon: "🐠", days: 0 },
    { value: "shrimp", name: "Shrimp (Sugpo)", icon: "🦐", days: 0 },
    { value: "horse", name: "Horse", icon: "🐴", days: 340 },
    { value: "goose", name: "Goose", icon: "🦢", days: 30 },
    { value: "bee", name: "Bee (Honey)", icon: "🐝", days: 0 },
    { value: "deer", name: "Deer", icon: "🦌", days: 205 },
    { value: "dog", name: "Dog", icon: "🐕", days: 63 },
    { value: "cat", name: "Cat", icon: "🐈", days: 65 },
    { value: "custom", name: "Custom Species", icon: "🐾", days: 0 },
];

// ---------- Branding ----------
function hexToHsl(hex) {
    const h = hex.replace("#", "");
    const r = parseInt(h.substring(0,2),16)/255, g = parseInt(h.substring(2,4),16)/255, b = parseInt(h.substring(4,6),16)/255;
    const max = Math.max(r,g,b), min = Math.min(r,g,b);
    let hh=0, s=0, l=(max+min)/2;
    if (max !== min) {
        const d = max-min;
        s = l > 0.5 ? d/(2-max-min) : d/(max+min);
        switch(max) {
            case r: hh = (g-b)/d + (g<b?6:0); break;
            case g: hh = (b-r)/d + 2; break;
            default: hh = (r-g)/d + 4;
        }
        hh *= 60;
    }
    return `${Math.round(hh)} ${Math.round(s*100)}% ${Math.round(l*100)}%`;
}

function BrandingTab() {
    const [farm, setFarm] = useState(null);
    const [primary, setPrimary] = useState("#2D6A4F");
    const [accent, setAccent] = useState("#A95C3E");
    const [uploading, setUploading] = useState(false);
    useEffect(() => { api.get("/farm").then(r => { setFarm(r.data); if (r.data.primary_hex) setPrimary(r.data.primary_hex); if (r.data.accent_hex) setAccent(r.data.accent_hex); }); }, []);
    if (!farm) return null;

    const applyLive = (p, a) => {
        document.documentElement.style.setProperty("--primary", hexToHsl(p));
        document.documentElement.style.setProperty("--accent", hexToHsl(a));
    };
    const save = async () => {
        const body = { ...farm, primary_hex: primary, accent_hex: accent, primary_hsl: hexToHsl(primary), accent_hsl: hexToHsl(accent) };
        await api.put("/farm", body);
        applyLive(primary, accent);
        toast.success("Branding saved");
    };
    const upload = async (file) => {
        setUploading(true);
        const fd = new FormData(); fd.append("file", file);
        const r = await api.post("/upload", fd);
        const url = apiFileUrl(r.data.id);
        const next = { ...farm, logo_url: url };
        await api.put("/farm", next); setFarm(next);
        toast.success("Logo uploaded"); setUploading(false);
    };
    return (
        <Card className="p-6 space-y-6" data-testid="branding-tab">
            <h3 className="text-xl font-semibold">{i18n.t("batch4.d17dc3843de8")}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                    <Label className="tiny-label">{i18n.t("batch4.d3dcce7d1024")}</Label>
                    <div className="flex gap-3 mt-2">
                        <Input type="color" value={primary} onChange={e=>{setPrimary(e.target.value); applyLive(e.target.value, accent);}} className="h-12 w-20" data-testid="brand-primary-color" />
                        <Input value={primary} onChange={e=>setPrimary(e.target.value)} className="h-12 flex-1 font-mono" />
                    </div>
                </div>
                <div>
                    <Label className="tiny-label">{i18n.t("batch4.2bef525f9fb9")}</Label>
                    <div className="flex gap-3 mt-2">
                        <Input type="color" value={accent} onChange={e=>{setAccent(e.target.value); applyLive(primary, e.target.value);}} className="h-12 w-20" data-testid="brand-accent-color" />
                        <Input value={accent} onChange={e=>setAccent(e.target.value)} className="h-12 flex-1 font-mono" />
                    </div>
                </div>
            </div>
            <div>
                <Label className="tiny-label">{i18n.t("batch4.2170afc55604")}</Label>
                <div className="flex items-center gap-4 mt-2">
                    {farm.logo_url && <img src={farm.logo_url} alt={i18n.t("batch4.5807dd602664")} className="w-24 h-24 rounded-xl object-cover border border-border" />}
                    <Input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} className="h-12" data-testid="brand-logo-upload" />
                </div>
                {uploading && <div className="text-sm text-muted-foreground mt-2">{i18n.t("batch4.070e328ec8aa")}</div>}
            </div>
            <div className="flex gap-3">
                <div className="p-4 rounded-xl bg-primary text-primary-foreground font-semibold">{i18n.t("batch4.4e26111f7ffa")}</div>
                <div className="p-4 rounded-xl bg-accent text-accent-foreground font-semibold">{i18n.t("batch4.856bf39480ed")}</div>
            </div>
            <Button onClick={save} className="h-12" data-testid="save-branding-btn">{i18n.t("batch4.b05b84880e32")}</Button>
        </Card>
    );
}

// ---------- Custom Metrics ----------
function CustomMetricsTab() {
    const [items, setItems] = useState([]);
    const [dlg, setDlg] = useState(null);
    const load = () => api.get("/custom-metrics").then(r => setItems(r.data));
    useEffect(() => { load(); }, []);
    const save = async () => {
        if (dlg.id) await api.put(`/custom-metrics/${dlg.id}`, dlg); else await api.post("/custom-metrics", dlg);
        toast.success("Saved"); setDlg(null); load();
    };
    const del = async (it) => { if (window.confirm("Delete?")) { await api.delete(`/custom-metrics/${it.id}`); load(); } };
    const COLLS = ["animals","transactions","sales","inventory_items","health_records","breeding_records"];
    const AGGS = ["count","sum","avg"];
    return (<>
        <ConfigTable
            title={i18n.t("batch4.924c045d68d5")} testid="metric"
            columns={["Key","Label","Collection","Agg","Field"]}
            items={items}
            onAdd={()=>setDlg({ metric_key: "", label: "", collection: "animals", aggregation: "count", field: "" })}
            onEdit={setDlg} onDelete={del} addLabel="Add Metric"
            renderRow={(it)=>(<>
                <TableCell className="font-mono">{it.metric_key}</TableCell>
                <TableCell className="font-semibold">{it.label}</TableCell>
                <TableCell>{it.collection}</TableCell>
                <TableCell>{it.aggregation}</TableCell>
                <TableCell className="font-mono text-xs">{it.field}</TableCell>
            </>)}
        />
        <Dialog open={!!dlg} onOpenChange={o=>!o&&setDlg(null)}>
            <DialogContent><DialogHeader><DialogTitle>{dlg?.id ? "Edit" : "New"} {i18n.t("batch4.6111fbdb4f78")}</DialogTitle></DialogHeader>
                {dlg && <div className="space-y-4">
                    <div><Label className="tiny-label">{i18n.t("batch4.fe6434c4ba45")}</Label><Input value={dlg.metric_key} onChange={e=>setDlg({...dlg,metric_key:e.target.value})} className="h-12 mt-2" placeholder={i18n.t("batch4.285f62b21200")} data-testid="metric-key-input"/></div>
                    <div><Label className="tiny-label">{i18n.t("batch4.74341e3c271d")}</Label><Input value={dlg.label} onChange={e=>setDlg({...dlg,label:e.target.value})} className="h-12 mt-2"/></div>
                    <div><Label className="tiny-label">{i18n.t("batch4.30c54a96f801")}</Label>
                        <Select value={dlg.collection} onValueChange={v=>setDlg({...dlg,collection:v})}>
                            <SelectTrigger className="h-12 mt-2"><SelectValue /></SelectTrigger>
                            <SelectContent>{COLLS.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div><Label className="tiny-label">{i18n.t("batch4.b9ba037c0e4c")}</Label>
                        <Select value={dlg.aggregation} onValueChange={v=>setDlg({...dlg,aggregation:v})}>
                            <SelectTrigger className="h-12 mt-2"><SelectValue /></SelectTrigger>
                            <SelectContent>{AGGS.map(a=><SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    {dlg.aggregation !== "count" && <div><Label className="tiny-label">{i18n.t("batch4.e81a853e3e60")}</Label><Input value={dlg.field} onChange={e=>setDlg({...dlg,field:e.target.value})} className="h-12 mt-2" placeholder={i18n.t("batch4.a1993bdd2c70")}/></div>}
                    <Button onClick={save} className="h-12 w-full" data-testid="save-metric-btn">{i18n.t("batch4.efc007a393f6")}</Button>
                </div>}
            </DialogContent>
        </Dialog>
    </>);
}

// ---------- Farm Info ----------
function FarmField({ farm, setFarm, k, label, type = "text" }) {
    return (
        <div>
            <Label className="tiny-label">{label}</Label>
            <Input value={farm[k] || ""} type={type} onChange={(e) => setFarm({ ...farm, [k]: e.target.value })} className="h-12 mt-2" data-testid={`farm-${k}`} />
        </div>
    );
}

function FarmInfoTab() {
    const [farm, setFarm] = useState(null);
    useEffect(() => { api.get("/farm").then(r => setFarm(r.data)); }, []);
    if (!farm) return null;
    const save = async () => {
        await api.put("/farm", farm);
        toast.success("Farm updated");
    };
    return (
        <div className="space-y-6">
            <Card className="p-6 space-y-6" data-testid="farm-info-tab">
                <h3 className="text-xl font-semibold">{i18n.t("batch4.92c79c26941e")}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FarmField farm={farm} setFarm={setFarm} k="name" label={i18n.t("batch4.1af3b7799f1c")} />
                    <FarmField farm={farm} setFarm={setFarm} k="owner_name" label={i18n.t("batch4.15af1b1072dd")} />
                    <FarmField farm={farm} setFarm={setFarm} k="contact_email" label={i18n.t("batch4.3142970c1419")} type="email" />
                    <FarmField farm={farm} setFarm={setFarm} k="contact_phone" label={i18n.t("batch4.0c4e1d04a4a1")} />
                    <FarmField farm={farm} setFarm={setFarm} k="address" label={i18n.t("batch4.d70f93df5e8f")} />
                    <FarmField farm={farm} setFarm={setFarm} k="gps" label={i18n.t("batch4.25c47fcbb74b")} />
                    <FarmField farm={farm} setFarm={setFarm} k="timezone" label={i18n.t("batch4.d1f7dc891b5e")} />
                    <FarmField farm={farm} setFarm={setFarm} k="currency" label={i18n.t("batch4.e070de224434")} />
                    <FarmField farm={farm} setFarm={setFarm} k="units" label={i18n.t("batch4.12748281e0f0")} />
                    <FarmField farm={farm} setFarm={setFarm} k="language" label={i18n.t("batch4.89b86ab0e66f")} />
                    <FarmField farm={farm} setFarm={setFarm} k="logo_url" label={i18n.t("batch4.523eeafcdf89")} />
                </div>
                <Button onClick={save} className="h-12" data-testid="save-farm-btn">{i18n.t("batch4.fa2984b367b8")}</Button>
            </Card>
        </div>
    );
}

// ---------- Species Manager ----------
function SpeciesManagerTab() {
    const [items, setItems] = useState([]);
    const [dlg, setDlg] = useState(null);
    const [vaccDlg, setVaccDlg] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [schedule, setSchedule] = useState([]);
    const load = () => api.get("/species").then(r => setItems(r.data));
    useEffect(() => { load(); }, []);
    const save = async () => {
        const normalizedName = dlg.name.trim().replace(/\s+/g, " ");
        const duplicate = items.some(item => item.id !== dlg.id && item.name.trim().replace(/\s+/g, " ").toLocaleLowerCase() === normalizedName.toLocaleLowerCase());
        if (!normalizedName) { toast.error("Species name is required"); return; }
        if (duplicate) { toast.error("A species with this name already exists"); return; }
        const { preset, ...values } = dlg;
        const body = { ...values, name: normalizedName, sort_order: Number(dlg.sort_order) || 0, gestation_days: Number(dlg.gestation_days) || 0 };
        try {
            if (dlg.id) await api.put(`/species/${dlg.id}`, body); else await api.post("/species", body);
            toast.success("Saved"); setDlg(null); await load();
        } catch (error) {
            toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Species could not be saved");
        }
    };
    const applyPreset = (value) => {
        const preset = SPECIES_PRESETS.find(item => item.value === value);
        if (preset) setDlg(current => ({ ...current, preset: value, name: preset.name, icon: preset.icon, gestation_days: preset.days }));
    };
    const del = async () => {
        if (!deleteTarget || deleting) return;
        setDeleting(true);
        try {
            await api.delete(`/species/${deleteTarget.id}`);
            toast.success(`${deleteTarget.name} moved to Recycle Bin`);
            setDeleteTarget(null);
            await load();
        } catch (error) {
            const detail = error.response?.data?.detail;
            if (error.response?.status === 404 && detail === "Active record not found") {
                setDeleteTarget(null);
                await load();
                toast.info("This species is no longer active. The Species list has been refreshed.");
                return;
            }
            const dependencies = Array.isArray(detail?.dependencies)
                ? detail.dependencies.map(item => `${item.label || item.collection?.replaceAll("_", " ") || "Related records"} (${item.count})`).join(", ")
                : "";
            toast.error(`${formatApiErrorDetail(detail) || "Species could not be deleted"}${dependencies ? ` Preventing records: ${dependencies}.` : ""}`);
        } finally {
            setDeleting(false);
        }
    };

    const openVacc = async (sp) => {
        const s = await api.get(`/species/${sp.id}/vaccination-schedule`).then(r => r.data);
        const withRids = (Array.isArray(s) ? s : []).map((r, i) => r._rid ? r : { ...r, _rid: `rid-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 6)}` });
        setSchedule(withRids);
        setVaccDlg(sp);
    };
    const saveSchedule = async () => {
        await api.put(`/species/${vaccDlg.id}/vaccination-schedule`, { schedule });
        toast.success("Schedule saved. New animals will auto-generate these tasks.");
        setVaccDlg(null);
    };
    return (
        <>
            <ConfigTable
                title={i18n.t("batch4.83a7238826ab")} testid="species"
                columns={["Icon", "Name", "Gestation", "Vaccinations", "Active"]}
                items={items} onAdd={() => setDlg({ preset: "custom", name: "", icon: "🐾", color: "#8ECAE6", active: true, sort_order: items.length, gestation_days: 0 })}
                onEdit={setDlg} onDelete={setDeleteTarget} addLabel="Add Species" deleteLabel="Delete"
                renderRow={(it) => (<>
                    <TableCell className="text-2xl">{it.icon}</TableCell>
                    <TableCell className="font-semibold">{it.name}</TableCell>
                    <TableCell className="font-mono">{it.gestation_days || 0}{i18n.t("batch4.3c363836cf4e")}</TableCell>
                    <TableCell>
                        <Button size="sm" variant="outline" onClick={() => openVacc(it)} data-testid={`vacc-btn-${it.id}`}>
                            💉 {(it.vaccination_schedule || []).length} {i18n.t("batch4.caa155adf81f")}
                        </Button>
                    </TableCell>
                    <TableCell>{it.active ? "Yes" : "No"}</TableCell>
                </>)}
            />
            <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
                <DialogContent><DialogHeader><DialogTitle>{dlg?.id ? "Edit" : "New"} {i18n.t("batch4.83a7238826ab")}</DialogTitle></DialogHeader>
                    {dlg && <div className="space-y-4">
                        {!dlg.id && <div><Label className="tiny-label">Species preset</Label>
                            <Select value={dlg.preset || "custom"} onValueChange={applyPreset}>
                                <SelectTrigger className="h-12 mt-2" data-testid="species-preset-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{SPECIES_PRESETS.map(preset => <SelectItem key={preset.value} value={preset.value}>{preset.icon} {preset.name}</SelectItem>)}</SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground mt-1">Presets only prefill this form. Review and edit all values before saving.</p>
                        </div>}
                        <div><Label className="tiny-label">{i18n.t("batch4.709a23220f2c")}</Label><Input value={dlg.name} onChange={e=>setDlg({...dlg,name:e.target.value})} className="h-12 mt-2" data-testid="species-name-input"/></div>
                        <div><Label className="tiny-label">{i18n.t("batch4.2b894bd144da")}</Label><div className="mt-2"><SpeciesIconPicker value={dlg.icon} onChange={icon => setDlg({...dlg, icon})} id="admin-species-icon" /></div></div>
                        <div><Label className="tiny-label">{i18n.t("batch4.1d0c8304baed")}</Label><Input type="color" value={dlg.color} onChange={e=>setDlg({...dlg,color:e.target.value})} className="h-12 mt-2"/></div>
                        <div><Label className="tiny-label">{i18n.t("batch4.38683def9932")}</Label><Input type="number" value={dlg.gestation_days} onChange={e=>setDlg({...dlg,gestation_days:e.target.value})} className="h-12 mt-2"/></div>
                        <div><Label className="tiny-label">{i18n.t("batch4.5b5417c8f809")}</Label><Input type="number" value={dlg.sort_order} onChange={e=>setDlg({...dlg,sort_order:e.target.value})} className="h-12 mt-2"/></div>
                        <div className="flex items-center gap-3"><Switch checked={!!dlg.active} onCheckedChange={v=>setDlg({...dlg,active:v})} data-testid="species-active-switch"/><Label>{i18n.t("batch4.a733b809d2f1")}</Label></div>
                        <Button onClick={save} className="h-12 w-full" data-testid="save-species-btn">{i18n.t("batch4.efc007a393f6")}</Button>
                    </div>}
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}>
                <AlertDialogContent data-testid="admin-delete-species-confirmation">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete species “{deleteTarget?.name}”?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This moves the species and its exclusively owned setup fields to Recycle Bin. Animals and real operational history will always block deletion.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction disabled={deleting} onClick={(event) => { event.preventDefault(); del(); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            {deleting ? "Deleting..." : "Delete Species"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Dialog open={!!vaccDlg} onOpenChange={(o) => !o && setVaccDlg(null)}>
                <DialogContent className="max-w-xl" data-testid="vacc-schedule-dialog">
                    <DialogHeader>
                        <DialogTitle>{i18n.t("batch4.03c283192cfa")} {vaccDlg?.name}</DialogTitle>
                        <DialogDescription>{i18n.t("batch4.ce1911fa1f05")}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="max-h-80 overflow-y-auto space-y-2">
                            {schedule.map((row, i) => (
                                <div key={row._rid || `rid-${i}`} className="grid grid-cols-12 gap-2 items-center">
                                    <Input placeholder={i18n.t("batch4.574c2d9fe3b6")} value={row.name} onChange={e => setSchedule(schedule.map((r, j) => j === i ? { ...r, name: e.target.value } : r))} className="h-10 col-span-4" />
                                    <Input type="number" placeholder={i18n.t("batch4.987b9ced08d4")} value={row.day_offset} onChange={e => setSchedule(schedule.map((r, j) => j === i ? { ...r, day_offset: e.target.value } : r))} className="h-10 col-span-2 font-mono" />
                                    <Input placeholder={i18n.t("batch4.da7f99e8cbbf")} value={row.dose} onChange={e => setSchedule(schedule.map((r, j) => j === i ? { ...r, dose: e.target.value } : r))} className="h-10 col-span-3" />
                                    <Input placeholder={i18n.t("batch4.70440046a3dc")} value={row.notes} onChange={e => setSchedule(schedule.map((r, j) => j === i ? { ...r, notes: e.target.value } : r))} className="h-10 col-span-2" />
                                    <Button size="sm" variant="destructive" onClick={() => setSchedule(schedule.filter((_, j) => j !== i))} className="h-10 col-span-1">×</Button>
                                </div>
                            ))}
                            {schedule.length === 0 && <div className="text-center py-6 text-sm text-muted-foreground">{i18n.t("batch4.5de21c05bd89")}</div>}
                        </div>
                        <Button variant="outline" onClick={() => setSchedule([...schedule, { _rid: `rid-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, name: "", day_offset: 0, dose: "", notes: "" }])} className="w-full" data-testid="add-vacc-rule-btn">
                            {i18n.t("batch4.cda8cc502497")}
                        </Button>
                        <Button onClick={saveSchedule} className="w-full h-11" data-testid="save-vacc-schedule-btn">{i18n.t("batch4.e9bbbb24aa89")}</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

// ---------- Field Builder ----------
function FieldBuilderTab() {
    const [species, setSpecies] = useState([]);
    const [selected, setSelected] = useState("");
    const [items, setItems] = useState([]);
    const [dlg, setDlg] = useState(null);
    const [fieldAction, setFieldAction] = useState(null);
    useEffect(() => { api.get("/species").then(r => { setSpecies(r.data); if (r.data[0]) setSelected(r.data[0].id); }); }, []);
    const load = () => { if (selected) api.get(`/field-definitions?species_id=${selected}&include_retired=true`).then(r => setItems(r.data)); };
    useEffect(() => { load(); }, [selected]);
    const save = async () => {
        const body = { ...dlg, species_id: selected, sort_order: Number(dlg.sort_order) || 0, options: (dlg.options_str||"").split(",").map(s=>s.trim()).filter(Boolean) };
        delete body.options_str;
        if (dlg.id) await api.put(`/field-definitions/${dlg.id}`, body); else await api.post("/field-definitions", body);
        toast.success("Saved"); setDlg(null); load();
    };
    const prepareFieldAction = async (it) => {
        try {
            const response = await api.get(`/field-definitions/${it.id}/dependencies`);
            setFieldAction({ field: it, ...response.data });
        } catch (error) {
            toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Could not inspect field usage");
        }
    };
    const confirmFieldAction = async () => {
        if (!fieldAction) return;
        try {
            if (fieldAction.count > 0) {
                await api.post(`/field-definitions/${fieldAction.field.id}/retire`);
                toast.success("Field retired; historical values were preserved");
            } else {
                await api.delete(`/field-definitions/${fieldAction.field.id}`);
                toast.success("Unused field deleted");
            }
            setFieldAction(null);
            load();
        } catch (error) {
            toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Field action failed");
        }
    };
    const restoreField = async (it) => {
        try {
            await api.post(`/field-definitions/${it.id}/restore`);
            toast.success("Field restored");
            load();
        } catch (error) {
            toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Field restore failed");
        }
    };
    return (
        <div className="space-y-4" data-testid="field-builder-tab">
            <Card className="p-4">
                <Label className="tiny-label">{i18n.t("batch4.83a7238826ab")}</Label>
                <Select value={selected} onValueChange={setSelected}>
                    <SelectTrigger className="h-12 mt-2" data-testid="field-species-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{species.map(s => <SelectItem key={s.id} value={s.id}>{s.icon} {s.name}</SelectItem>)}</SelectContent>
                </Select>
            </Card>
            <ConfigTable
                title={i18n.t("batch4.143b179529ae")} testid="field"
                columns={["Key", "Label", "Type", "Section", "Required", "Status"]}
                items={items}
                onAdd={() => setDlg({ field_key: "", label: "", field_type: "text", section: "General", required: false, sort_order: items.length, options_str: "" })}
                onEdit={(it) => setDlg({ ...it, options_str: (it.options || []).join(", ") })}
                onDelete={prepareFieldAction} addLabel="Add Field"
                renderActions={(it) => it.active === false || it.retired_at ? <Button size="sm" variant="outline" onClick={() => restoreField(it)}>Restore</Button> : null}
                renderRow={(it) => (<>
                    <TableCell className="font-mono">{it.field_key}</TableCell>
                    <TableCell className="font-semibold">{it.label}</TableCell>
                    <TableCell>{it.field_type}</TableCell>
                    <TableCell>{it.section}</TableCell>
                    <TableCell>{it.required ? "Yes" : "No"}</TableCell>
                    <TableCell>{it.field_key === "weight_kg" || it.is_system ? "System / Core" : it.active === false || it.retired_at ? "Retired" : "Custom / Active"}</TableCell>
                </>)}
            />
            <Dialog open={!!dlg} onOpenChange={o => !o && setDlg(null)}>
                <DialogContent><DialogHeader><DialogTitle>{dlg?.id ? "Edit" : "New"} {i18n.t("batch4.c326a4660b67")}</DialogTitle></DialogHeader>
                    {dlg && <div className="space-y-4">
                        <div><Label className="tiny-label">{i18n.t("batch4.7cd5b9ea84fa")}</Label><Input value={dlg.field_key} onChange={e=>setDlg({...dlg,field_key:e.target.value})} className="h-12 mt-2" data-testid="field-key-input"/></div>
                        <div><Label className="tiny-label">{i18n.t("batch4.74341e3c271d")}</Label><Input value={dlg.label} onChange={e=>setDlg({...dlg,label:e.target.value})} className="h-12 mt-2" data-testid="field-label-input"/></div>
                        <div><Label className="tiny-label">{i18n.t("batch4.3deb74565196")}</Label>
                            <Select value={dlg.field_type} onValueChange={v=>setDlg({...dlg,field_type:v})}>
                                <SelectTrigger className="h-12 mt-2" data-testid="field-type-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {["text","number","date","boolean","dropdown","photo","gps"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        {dlg.field_type === "dropdown" && <div><Label className="tiny-label">{i18n.t("batch4.5a70e788e5eb")}</Label><Input value={dlg.options_str} onChange={e=>setDlg({...dlg,options_str:e.target.value})} className="h-12 mt-2"/></div>}
                        <div><Label className="tiny-label">{i18n.t("batch4.f2c6b564bd81")}</Label><Input value={dlg.section} onChange={e=>setDlg({...dlg,section:e.target.value})} className="h-12 mt-2"/></div>
                        <div><Label className="tiny-label">{i18n.t("batch4.5b5417c8f809")}</Label><Input type="number" value={dlg.sort_order} onChange={e=>setDlg({...dlg,sort_order:e.target.value})} className="h-12 mt-2"/></div>
                        <div className="flex items-center gap-3"><Switch checked={!!dlg.required} onCheckedChange={v=>setDlg({...dlg,required:v})}/><Label>{i18n.t("batch4.eed6bfb41051")}</Label></div>
                        <Button onClick={save} className="h-12 w-full" data-testid="save-field-btn">{i18n.t("batch4.efc007a393f6")}</Button>
                    </div>}
                </DialogContent>
            </Dialog>
            <AlertDialog open={!!fieldAction} onOpenChange={(open) => !open && setFieldAction(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{fieldAction?.count ? `Retire custom field '${fieldAction?.field?.label}'?` : `Delete custom field '${fieldAction?.field?.label}'?`}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {fieldAction?.core
                                ? "This is a core system field and cannot be deleted or retired."
                                : fieldAction?.count
                                    ? `This field is used by ${fieldAction.count} record(s): ${(fieldAction.dependencies || []).map((item) => `${item.label} (${item.count})`).join(", ")}. Retiring removes it from new data entry while preserving historical values.`
                                    : "This field has no stored data or dependencies. This action will remove the field definition."}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        {!fieldAction?.core && <AlertDialogAction onClick={confirmFieldAction}>{fieldAction?.count ? "Retire Field" : "Delete Field"}</AlertDialogAction>}
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

// ---------- Dashboard Builder ----------
function DashboardBuilderTab() {
    const [items, setItems] = useState([]);
    const [customMetrics, setCustomMetrics] = useState([]);
    const [dlg, setDlg] = useState(null);
    const load = () => api.get("/dashboard-cards").then(r => setItems(r.data));
    useEffect(() => { load(); api.get("/custom-metrics").then(r => setCustomMetrics(r.data)); }, []);
    const BUILTIN = [
        "total_animals",
        "total_species",
        "pregnant_animals",
        "mortality",
        "income_this_month",
        "expenses_this_month",
        "profit_this_month",
        "feed_stock",
        "low_stock_items",
        "upcoming_reminders",
        "sales_this_month",
        "active_animals",
        "market_ready_animals",
        "births_this_month",
        "mortality_this_month",
        "feed_inventory_count",
        "inventory_value",
        "monthly_income",
        "monthly_expenses",
        "monthly_profit",
        "present_employees_today",
    ];
    const METRICS = [...BUILTIN, ...customMetrics.map(m => m.metric_key)];
    const save = async () => {
        const body = { ...dlg, position: Number(dlg.position) || 0 };
        if (dlg.id) await api.put(`/dashboard-cards/${dlg.id}`, body); else await api.post("/dashboard-cards", body);
        toast.success("Saved"); setDlg(null); load();
    };
    const del = async (it) => { if (window.confirm("Delete?")) { await api.delete(`/dashboard-cards/${it.id}`); load(); } };
    const move = async (it, dir) => {
        const idx = items.findIndex(x => x.id === it.id);
        const target = items[idx + dir];
        if (!target) return;
        await api.put(`/dashboard-cards/${it.id}`, { ...it, position: target.position });
        await api.put(`/dashboard-cards/${target.id}`, { ...target, position: it.position });
        load();
    };
    return (<>
        <Card className="p-6" data-testid="config-dashcard">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold">{i18n.t("batch4.da2e1b8c194d")}</h3>
                <Button onClick={() => setDlg({ metric_key: BUILTIN[0], title: "", card_type: "kpi", chart_type: "", size: "sm", position: items.length })} className="h-11" data-testid="add-dashcard-btn"><Plus className="w-4 h-4 mr-2" />{i18n.t("batch4.70b38feb2a35")}</Button>
            </div>
            <Table>
                <TableHeader><TableRow><TableHead>{i18n.t("batch4.1d75774c0f96")}</TableHead><TableHead>{i18n.t("batch4.768e0c1c6957")}</TableHead><TableHead>{i18n.t("batch4.3deb74565196")}</TableHead><TableHead>{i18n.t("batch4.b2bb7604c825")}</TableHead><TableHead>{i18n.t("batch4.b7152342a267")}</TableHead><TableHead className="text-right">{i18n.t("batch4.c3cd636a585b")}</TableHead></TableRow></TableHeader>
                <TableBody>
                    {items.map((it, i) => (
                        <TableRow key={it.id}>
                            <TableCell className="font-mono">{it.position}</TableCell>
                            <TableCell className="font-semibold">{it.title}</TableCell>
                            <TableCell>{it.card_type || "kpi"}{it.chart_type ? ` (${it.chart_type})` : ""}</TableCell>
                            <TableCell className="font-mono text-xs">{it.metric_key}</TableCell>
                            <TableCell>{it.size}</TableCell>
                            <TableCell className="text-right space-x-1">
                                <Button size="sm" variant="outline" disabled={i===0} onClick={()=>move(it,-1)} data-testid={`move-up-${it.id}`}>↑</Button>
                                <Button size="sm" variant="outline" disabled={i===items.length-1} onClick={()=>move(it,1)} data-testid={`move-down-${it.id}`}>↓</Button>
                                <Button size="sm" variant="outline" onClick={() => setDlg(it)}><Edit2 className="w-4 h-4" /></Button>
                                <Button size="sm" variant="destructive" onClick={()=>del(it)}><Trash2 className="w-4 h-4" /></Button>
                            </TableCell>
                        </TableRow>
                    ))}
                    {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">{i18n.t("batch4.a1a1e1eb0b47")}</TableCell></TableRow>}
                </TableBody>
            </Table>
        </Card>
        <Dialog open={!!dlg} onOpenChange={o=>!o && setDlg(null)}>
            <DialogContent><DialogHeader><DialogTitle>{dlg?.id ? "Edit" : "New"} {i18n.t("batch4.4d4ce73b1566")}</DialogTitle></DialogHeader>
                {dlg && <div className="space-y-4">
                    <div><Label className="tiny-label">{i18n.t("batch4.768e0c1c6957")}</Label><Input value={dlg.title} onChange={e=>setDlg({...dlg,title:e.target.value})} className="h-12 mt-2" data-testid="card-title-input"/></div>
                    <div><Label className="tiny-label">{i18n.t("batch4.b0b1033a5d0c")}</Label>
                        <Select value={dlg.card_type || "kpi"} onValueChange={v=>setDlg({...dlg,card_type:v})}>
                            <SelectTrigger className="h-12 mt-2" data-testid="card-type-select"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="kpi">{i18n.t("batch4.24230b5f31bb")}</SelectItem><SelectItem value="chart">{i18n.t("batch4.66c7734789e1")}</SelectItem></SelectContent>
                        </Select>
                    </div>
                    {dlg.card_type === "chart" ? (
                        <div><Label className="tiny-label">{i18n.t("batch4.d8c107902a1e")}</Label>
                            <Select value={dlg.chart_type || "pie"} onValueChange={v=>setDlg({...dlg,chart_type:v})}>
                                <SelectTrigger className="h-12 mt-2"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="pie">{i18n.t("batch4.dacfa24d0868")}</SelectItem>
                                    <SelectItem value="line">{i18n.t("batch4.0ee6f3decb15")}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    ) : (
                        <div><Label className="tiny-label">{i18n.t("batch4.b2bb7604c825")}</Label>
                            <Select value={dlg.metric_key} onValueChange={v=>setDlg({...dlg,metric_key:v})}>
                                <SelectTrigger className="h-12 mt-2" data-testid="card-metric-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{METRICS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    )}
                    <div><Label className="tiny-label">{i18n.t("batch4.b7152342a267")}</Label>
                        <Select value={dlg.size} onValueChange={v=>setDlg({...dlg,size:v})}>
                            <SelectTrigger className="h-12 mt-2"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="sm">{i18n.t("batch4.3248151a7cdd")}</SelectItem>
                                <SelectItem value="md">{i18n.t("batch4.717606b3ddfb")}</SelectItem>
                                <SelectItem value="lg">{i18n.t("batch4.b09f1110b8bf")}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div><Label className="tiny-label">{i18n.t("batch4.cf1c85adba54")}</Label><Input type="number" value={dlg.position} onChange={e=>setDlg({...dlg,position:e.target.value})} className="h-12 mt-2"/></div>
                    <Button onClick={save} className="h-12 w-full" data-testid="save-card-btn">{i18n.t("batch4.efc007a393f6")}</Button>
                </div>}
            </DialogContent>
        </Dialog>
    </>);
}

// ---------- Formula Builder ----------
function FormulaBuilderTab() {
    const [items, setItems] = useState([]);
    const [species, setSpecies] = useState([]);
    const [dlg, setDlg] = useState(null);
    useEffect(() => { api.get("/species").then(r => setSpecies(r.data)); api.get("/formulas").then(r => setItems(r.data)); }, []);
    const load = () => api.get("/formulas").then(r => setItems(r.data));
    const save = async () => {
        const body = { ...dlg, value: Number(dlg.value) || dlg.value };
        if (dlg.id) await api.put(`/formulas/${dlg.id}`, body); else await api.post("/formulas", body);
        toast.success("Saved"); setDlg(null); load();
    };
    const del = async (it) => { if (window.confirm("Delete?")) { await api.delete(`/formulas/${it.id}`); load(); } };
    const sName = (id) => species.find(s => s.id === id)?.name || "—";
    return (<>
        <ConfigTable
            title={i18n.t("batch4.23837eb44131")} testid="formula"
            columns={["Key", "Label", "Species", "Value", "Unit"]}
            items={items} onAdd={() => setDlg({ formula_key: "", label: "", species_id: "", value: 0, unit: "days" })}
            onEdit={setDlg} onDelete={del} addLabel="Add Formula"
            renderRow={(it) => (<>
                <TableCell className="font-mono">{it.formula_key}</TableCell>
                <TableCell>{it.label}</TableCell>
                <TableCell>{sName(it.species_id)}</TableCell>
                <TableCell className="font-mono">{it.value}</TableCell>
                <TableCell>{it.unit}</TableCell>
            </>)}
        />
        <Dialog open={!!dlg} onOpenChange={o=>!o&&setDlg(null)}>
            <DialogContent><DialogHeader><DialogTitle>{dlg?.id ? "Edit" : "New"} {i18n.t("batch4.720f58fb1f93")}</DialogTitle></DialogHeader>
                {dlg && <div className="space-y-4">
                    <div><Label className="tiny-label">{i18n.t("batch4.c67dd20ee842")}</Label><Input value={dlg.formula_key} onChange={e=>setDlg({...dlg,formula_key:e.target.value})} className="h-12 mt-2" data-testid="formula-key-input"/></div>
                    <div><Label className="tiny-label">{i18n.t("batch4.74341e3c271d")}</Label><Input value={dlg.label} onChange={e=>setDlg({...dlg,label:e.target.value})} className="h-12 mt-2"/></div>
                    <div><Label className="tiny-label">{i18n.t("batch4.93a23b2c70b0")}</Label>
                        <Select value={dlg.species_id || "none"} onValueChange={v=>setDlg({...dlg,species_id: v==="none"?"":v})}>
                            <SelectTrigger className="h-12 mt-2"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="none">{i18n.t("batch4.3b906ae77c48")}</SelectItem>{species.map(s=><SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div><Label className="tiny-label">{i18n.t("batch4.aa3e02d38002")}</Label><Input value={dlg.value} onChange={e=>setDlg({...dlg,value:e.target.value})} className="h-12 mt-2"/></div>
                    <div><Label className="tiny-label">{i18n.t("batch4.f6b935ab3371")}</Label><Input value={dlg.unit} onChange={e=>setDlg({...dlg,unit:e.target.value})} className="h-12 mt-2"/></div>
                    <Button onClick={save} className="h-12 w-full" data-testid="save-formula-btn">{i18n.t("batch4.efc007a393f6")}</Button>
                </div>}
            </DialogContent>
        </Dialog>
    </>);
}

// ---------- Role & Permission Manager ----------
const PERMS = ["view","add","edit","delete","approve","export","manage_users","manage_finance","manage_inventory","manage_reports","manage_settings"];

function RolesTab() {
    const [items, setItems] = useState([]);
    const [dlg, setDlg] = useState(null);
    const load = () => api.get("/roles").then(r => setItems(r.data));
    useEffect(() => { load(); }, []);
    const save = async () => {
        if (dlg.id) await api.put(`/roles/${dlg.id}`, dlg); else await api.post("/roles", dlg);
        toast.success("Saved"); setDlg(null); load();
    };
    const del = async (it) => { if (window.confirm("Delete role?")) { await api.delete(`/roles/${it.id}`); load(); } };
    return (<>
        <ConfigTable
            title={i18n.t("batch4.78f58c62ad53")} testid="role"
            columns={["Name", "Permissions"]}
            items={items} onAdd={() => setDlg({ name: "", permissions: { view: true } })}
            onEdit={setDlg} onDelete={del} addLabel="Add Role"
            renderRow={(it) => (<>
                <TableCell className="font-semibold">{it.name}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{Object.entries(it.permissions || {}).filter(([_,v])=>v).map(([k])=>k).join(", ")}</TableCell>
            </>)}
        />
        <Dialog open={!!dlg} onOpenChange={o=>!o&&setDlg(null)}>
            <DialogContent className="max-w-2xl"><DialogHeader><DialogTitle>{dlg?.id ? "Edit" : "New"} {i18n.t("batch4.c3f104d13657")}</DialogTitle></DialogHeader>
                {dlg && <div className="space-y-4">
                    <div><Label className="tiny-label">{i18n.t("batch4.709a23220f2c")}</Label><Input value={dlg.name} onChange={e=>setDlg({...dlg,name:e.target.value})} className="h-12 mt-2" data-testid="role-name-input"/></div>
                    <div>
                        <Label className="tiny-label">{i18n.t("batch4.d06d55570938")}</Label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
                            {PERMS.map(p => (
                                <label key={p} className="flex items-center gap-2 p-3 rounded-xl border border-border cursor-pointer">
                                    <Checkbox checked={!!dlg.permissions?.[p]} onCheckedChange={v=>setDlg({...dlg, permissions: {...(dlg.permissions||{}), [p]: !!v}})} data-testid={`role-perm-${p}`}/>
                                    <span className="text-sm">{p}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <Button onClick={save} className="h-12 w-full" data-testid="save-role-btn">{i18n.t("batch4.efc007a393f6")}</Button>
                </div>}
            </DialogContent>
        </Dialog>
    </>);
}

// ---------- Users management (Owner-only delete) ----------
function UsersTab() {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const load = async () => {
        const [u, r] = await Promise.all([
            api.get("/users").then(x => x.data),
            api.get("/roles").then(x => x.data),
        ]);
        setUsers(u || []); setRoles(r || []);
    };
    useEffect(() => { load(); }, []);
    const changeRole = async (uid, role_id) => {
        try { await api.put(`/users/${uid}/role`, { role_id }); toast.success("Role updated"); load(); }
        catch (e) { toast.error("Role update failed"); }
    };
    const del = async (u) => {
        if (!window.confirm(`Delete user "${u.email}"? This is irreversible.`)) return;
        try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    };
    return (
        <Card className="p-4" data-testid="users-tab">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="font-semibold">{i18n.t("batch4.b5600c5378e4")}{users.length})</h3>
                    <p className="text-xs text-muted-foreground">{i18n.t("batch4.2485e6eb1cba")}</p>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                        <tr className="text-left">
                            <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold">{i18n.t("batch4.6422dd054202")}</th>
                            <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold">{i18n.t("batch4.c3f104d13657")}</th>
                            <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold">{i18n.t("batch4.accf40c89baa")}</th>
                            <th className="p-2 text-right"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.id} className="border-t" data-testid={`user-row-${u.id}`}>
                                <td className="p-2">
                                    <div className="font-medium">{u.name || "—"}</div>
                                    <div className="text-xs text-muted-foreground">{u.email}</div>
                                </td>
                                <td className="p-2">
                                    <Select value={u.role_id || ""} onValueChange={(v) => changeRole(u.id, v)}>
                                        <SelectTrigger className="h-8 w-40" data-testid={`user-role-select-${u.id}`}>
                                            <SelectValue placeholder={u.role_name || "—"} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {roles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </td>
                                <td className="p-2 text-xs text-muted-foreground">{(u.created_at || "").slice(0, 10)}</td>
                                <td className="p-2 text-right">
                                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del(u)} data-testid={`delete-user-${u.id}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        {users.length === 0 && (
                            <tr><td colSpan={4} className="text-center text-muted-foreground py-6">{i18n.t("batch4.8270e6cbab43")}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}

// ---------- Workflow / Reminder simple builders ----------
function WorkflowTab() {
    const [wfs, setWfs] = useState([]);
    const [species, setSpecies] = useState([]);
    const [dlg, setDlg] = useState(null);
    useEffect(() => { api.get("/species").then(r => setSpecies(r.data)); load(); }, []);
    const load = () => api.get("/workflows").then(r => setWfs(r.data));
    const save = async () => {
        const body = { ...dlg, stages: (dlg.stages_str||"").split(",").map(s=>s.trim()).filter(Boolean) };
        delete body.stages_str;
        if (dlg.id) await api.put(`/workflows/${dlg.id}`, body); else await api.post("/workflows", body);
        toast.success("Saved"); setDlg(null); load();
    };
    const del = async (it) => { if (window.confirm("Delete?")) { await api.delete(`/workflows/${it.id}`); load(); } };
    return (<>
        <ConfigTable
            title={i18n.t("batch4.825ce9e97dc4")} testid="workflow" columns={["Name","Species","Stages"]}
            items={wfs} onAdd={() => setDlg({ name: "", species_id: "", stages_str: "" })}
            onEdit={(it)=>setDlg({...it, stages_str:(it.stages||[]).join(", ")})}
            onDelete={del} addLabel="Add Workflow"
            renderRow={(it) => (<>
                <TableCell className="font-semibold">{it.name}</TableCell>
                <TableCell>{species.find(s=>s.id===it.species_id)?.name || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{(it.stages||[]).join(" → ")}</TableCell>
            </>)}
        />
        <Dialog open={!!dlg} onOpenChange={o=>!o&&setDlg(null)}>
            <DialogContent><DialogHeader><DialogTitle>{dlg?.id ? "Edit" : "New"} {i18n.t("batch4.d7a484140f5f")}</DialogTitle></DialogHeader>
                {dlg && <div className="space-y-4">
                    <div><Label className="tiny-label">{i18n.t("batch4.709a23220f2c")}</Label><Input value={dlg.name} onChange={e=>setDlg({...dlg,name:e.target.value})} className="h-12 mt-2"/></div>
                    <div><Label className="tiny-label">{i18n.t("batch4.83a7238826ab")}</Label>
                        <Select value={dlg.species_id || "none"} onValueChange={v=>setDlg({...dlg,species_id: v==="none"?"":v})}>
                            <SelectTrigger className="h-12 mt-2"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="none">{i18n.t("batch4.3b906ae77c48")}</SelectItem>{species.map(s=><SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div><Label className="tiny-label">{i18n.t("batch4.45833e303084")}</Label><Input value={dlg.stages_str} onChange={e=>setDlg({...dlg,stages_str:e.target.value})} className="h-12 mt-2" placeholder={i18n.t("batch4.a26a5714a97f")}/></div>
                    <Button onClick={save} className="h-12 w-full" data-testid="save-workflow-btn">{i18n.t("batch4.efc007a393f6")}</Button>
                </div>}
            </DialogContent>
        </Dialog>
    </>);
}

function RemindersTab() {
    const [items, setItems] = useState([]);
    const [dlg, setDlg] = useState(null);
    const load = () => api.get("/reminders").then(r => setItems(r.data));
    useEffect(() => { load(); }, []);
    const save = async () => {
        if (dlg.id) await api.put(`/reminders/${dlg.id}`, dlg); else await api.post("/reminders", dlg);
        toast.success("Saved"); setDlg(null); load();
    };
    const del = async (it) => { if (window.confirm("Delete?")) { await api.delete(`/reminders/${it.id}`); load(); } };
    return (<>
        <ConfigTable
            title={i18n.t("batch4.ae8c3939382f")} testid="reminder" columns={["Title","Type","Due","Priority"]}
            items={items} onAdd={()=>setDlg({ title: "", type: "custom", due_date: "", priority: "medium", days_before: 3 })}
            onEdit={setDlg} onDelete={del} addLabel="Add Reminder"
            renderRow={(it)=>(<>
                <TableCell className="font-semibold">{it.title}</TableCell>
                <TableCell>{it.type}</TableCell>
                <TableCell className="font-mono">{it.due_date}</TableCell>
                <TableCell>{it.priority}</TableCell>
            </>)}
        />
        <Dialog open={!!dlg} onOpenChange={o=>!o&&setDlg(null)}>
            <DialogContent><DialogHeader><DialogTitle>{dlg?.id ? "Edit" : "New"} {i18n.t("batch4.b87a1929f78b")}</DialogTitle></DialogHeader>
                {dlg && <div className="space-y-4">
                    <div><Label className="tiny-label">{i18n.t("batch4.768e0c1c6957")}</Label><Input value={dlg.title} onChange={e=>setDlg({...dlg,title:e.target.value})} className="h-12 mt-2"/></div>
                    <div><Label className="tiny-label">{i18n.t("batch4.3deb74565196")}</Label>
                        <Select value={dlg.type} onValueChange={v=>setDlg({...dlg,type:v})}>
                            <SelectTrigger className="h-12 mt-2"><SelectValue /></SelectTrigger>
                            <SelectContent>{["vaccination","pregnancy_check","medicine_expiry","feed_low_stock","custom"].map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div><Label className="tiny-label">{i18n.t("batch4.a1b308ec704a")}</Label><Input type="date" value={dlg.due_date} onChange={e=>setDlg({...dlg,due_date:e.target.value})} className="h-12 mt-2"/></div>
                    <div><Label className="tiny-label">{i18n.t("batch4.886cbff9d9df")}</Label>
                        <Select value={dlg.priority} onValueChange={v=>setDlg({...dlg,priority:v})}>
                            <SelectTrigger className="h-12 mt-2"><SelectValue /></SelectTrigger>
                            <SelectContent>{["low","medium","high"].map(p=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <Button onClick={save} className="h-12 w-full" data-testid="save-reminder-btn">{i18n.t("batch4.efc007a393f6")}</Button>
                </div>}
            </DialogContent>
        </Dialog>
    </>);
}

// ---------- Main Admin Page ----------
export default function AdminSettingsPage() {
    const { can } = useAuth();
    const canExport = can("export");
    return (
        <div className="space-y-6" data-testid="admin-settings-page">
            <div className="flex items-center gap-3">
                <Settings2 className="w-8 h-8 text-primary" />
                <div>
                    <div className="tiny-label">{i18n.t("batch4.754164850f38")}</div>
                    <h1 className="text-4xl font-bold tracking-tight">{i18n.t("batch4.2bf73c3604d6")}</h1>
                </div>
            </div>
            <Tabs defaultValue="farm">
                <TabsList className="h-auto flex flex-wrap">
                    <TabsTrigger value="farm" className="h-10 px-4" data-testid="tab-farm">{i18n.t("batch4.db3ade5935b6")}</TabsTrigger>
                    <TabsTrigger value="branding" className="h-10 px-4" data-testid="tab-branding">{i18n.t("batch4.d17dc3843de8")}</TabsTrigger>
                    <TabsTrigger value="species" className="h-10 px-4" data-testid="tab-species-mgr">{i18n.t("batch4.83a7238826ab")}</TabsTrigger>
                    <TabsTrigger value="fields" className="h-10 px-4" data-testid="tab-fields">{i18n.t("batch4.e8b685271238")}</TabsTrigger>
                    <TabsTrigger value="dashboard" className="h-10 px-4" data-testid="tab-dashboard-builder">{i18n.t("batch4.d87f47b47e4d")}</TabsTrigger>
                    <TabsTrigger value="metrics" className="h-10 px-4" data-testid="tab-metrics">{i18n.t("batch4.ddf6a1f0ce8c")}</TabsTrigger>
                    <TabsTrigger value="roles" className="h-10 px-4" data-testid="tab-roles">{i18n.t("batch4.47dcc27d6e87")}</TabsTrigger>
                    <TabsTrigger value="users" className="h-10 px-4" data-testid="tab-users">{i18n.t("batch4.57f2b181d0a5")}</TabsTrigger>
                    <TabsTrigger value="customization" className="h-10 px-4" data-testid="tab-customization"><Layers className="w-4 h-4 mr-1" />{i18n.t("batch4.d7f0ac9e0072")}</TabsTrigger>
                    {canExport && <TabsTrigger value="export" className="h-10 px-4" data-testid="tab-export"><Database className="w-4 h-4 mr-1" />{i18n.t("batch4.fd369935a258")}</TabsTrigger>}
                    <TabsTrigger value="drive" className="h-10 px-4" data-testid="tab-drive"><Cloud className="w-4 h-4 mr-1" />{i18n.t("batch4.07c2964c41f4")}</TabsTrigger>
                </TabsList>
                <TabsContent value="farm" className="mt-6"><FarmInfoTab /></TabsContent>
                <TabsContent value="branding" className="mt-6"><BrandingTab /></TabsContent>
                <TabsContent value="species" className="mt-6"><SpeciesManagerTab /></TabsContent>
                <TabsContent value="fields" className="mt-6"><FieldBuilderTab /></TabsContent>
                <TabsContent value="dashboard" className="mt-6"><DashboardBuilderTab /></TabsContent>
                <TabsContent value="metrics" className="mt-6"><CustomMetricsTab /></TabsContent>
                <TabsContent value="roles" className="mt-6"><RolesTab /></TabsContent>
                <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
                <TabsContent value="customization" className="mt-6"><CustomizationTab /></TabsContent>
                {canExport && <TabsContent value="export" className="mt-6"><DataExportTab /></TabsContent>}
                <TabsContent value="drive" className="mt-6"><GoogleDriveTab /></TabsContent>
            </Tabs>
        </div>
    );
}
